/**
 * sl-p64h.9: Abstract base class for per-consumer paginated sessions.
 *
 * Extracts the common reactive shell shared by `MemberSearchSession` and
 * `DocGrantedUsersSession`: a `loadMore` effect with `exhaustMap` + `EMPTY`
 * gating on `hasMore$`, retry/error routing, and lifecycle (`reset`,
 * `dispose`). Subclasses provide page-fetching + page-handling and any
 * domain-specific cursor/filter state via `resetExtra`.
 */
import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { EMPTY, exhaustMap, tap } from 'rxjs';

export abstract class PaginatedSession<TItem, TPage> {
  protected readonly items$ = new LiveData<TItem[]>([]);
  readonly hasMore$ = new LiveData(true);
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<any>(null);

  readonly loadMore = effect(
    exhaustMap(() => {
      if (!this.hasMore$.value) {
        return EMPTY;
      }
      return fromPromise(signal => this.fetchPage(signal)).pipe(
        tap(page => this.handlePage(page)),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => this.isLoading$.setValue(true)),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  /**
   * Fetch one page. Subclasses MUST forward `signal` to the underlying
   * network call so that `loadMore.reset()` / `dispose()` and the rxjs
   * `exhaustMap` cancellation actually abort in-flight requests instead
   * of leaking. The signal is owned by `fromPromise` and aborts when the
   * inner observable is unsubscribed.
   */
  protected abstract fetchPage(signal: AbortSignal): Promise<TPage>;

  /**
   * Apply the fetched page to subclass state: append items to `items$`,
   * propagate `hasMore$`, advance any cursor / count fields. Runs inside
   * the `tap` step before retry/error/onComplete handlers.
   */
  protected abstract handlePage(page: TPage): void;

  /**
   * Hook for subclass-specific state (cursor, filter input, count) that
   * must be cleared on `reset`. Default no-op.
   */
  protected resetExtra(): void {}

  reset(): void {
    this.items$.setValue([]);
    this.hasMore$.setValue(true);
    this.isLoading$.setValue(false);
    this.error$.setValue(null);
    this.resetExtra();
    this.loadMore.reset();
  }

  dispose(): void {
    this.loadMore.unsubscribe();
  }
}
